import React, { useEffect, useState } from "react";
import {
  SearchIcon,
  IncomingIcon,
  CompletedIcon,
  RejectedIcon,
  PendingIcon,
} from "../../assets/svgs/Icons";
import { FiFilter } from "react-icons/fi";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Tab,
  Table,
  TableCaption,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Link,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";

import {
  Column,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  sortingFns,
  getSortedRowModel,
  FilterFn,
  SortingFn,
  ColumnDef,
  flexRender,
  FilterFns,
  createColumnHelper,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import moment from "moment/moment";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import isAllowed from "../../routing/isAllowed";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

import { getProcurementsForCurrentUser } from "./ProcurementSlice";

function AppliedProcurements() {
  const [status, setStatus] = useState("idle");
  const { appliedList, isloading } = useSelector((state) => state.procurement);
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.auth);
  const [columnFilters, setColumnFilters] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();

  const getList = () => {
    try {
      setStatus("pending");
      dispatch(getProcurementsForCurrentUser());
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      console.lof(error);
    }
  };

  const updateIncomingData = () => {
    const updatedData = appliedList?.map((d, index) => {
      return {
        ...d,
        srNo: index + 1,
        actions: "actions",
        date: `${moment(d?.createdAt).format("DD-MM-YYYY")}`,
        farmerName: `${d?.procurement_supplier?.firstName} ${d?.procurement_supplier?.lastName}`,
        statusA: d?.status === "INCOMING" ? "PENDING" : d?.status,
      };
    });
    setUpdatedData(updatedData);
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("srNo", {
      cell: (info) => info.getValue(),
      header: "srNo",
    }),
    columnHelper.accessor("date", {
      cell: (info) => info.getValue(),
      header: "Date",
      sortType: (a, b) => {
        var a1 = new Date(a).getTime();
        var b1 = new Date(b).getTime();
        if (a1 < b1) return 1;
        else if (a1 > b1) return -1;
        else return 0;
      },
    }),
    columnHelper.accessor("id", {
      cell: (info) => info.getValue(),
      header: "Procurement ID",
    }),
    columnHelper.accessor("procurement_unit.name", {
      cell: (info) => info.getValue(),
      header: "Unit",
    }),
    columnHelper.accessor("procurement_program.name", {
      cell: (info) => info.getValue(),
      header: "Program",
    }),
    columnHelper.accessor("product_productClassification.name", {
      cell: (info) => info.getValue(),
      header: "Product Classification",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor(
      (row) => {
        if (row.procurement_supplier && row.procurement_supplier.name) {
          return row.procurement_supplier.name;
        } else if (
          row.procurement_supplier &&
          row.procurement_supplier.firstName &&
          row.procurement_supplier.lastName
        ) {
          return `${row.procurement_supplier.firstName} ${row.procurement_supplier.lastName}`;
        }
        return "";
      },
      {
        cell: (info) => info.getValue(),
        header: "Supplier Name",
        // meta: {
        //   isNumeric: true,
        // },
      }
    ),

    columnHelper.accessor("stockQuantity.grossWeight", {
      cell: (info) => info.getValue(),
      header: "Gross Weight (kg)",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("stockQuantity.netWeight", {
      cell: (info) => info.getValue(),
      header: "Net Weight (kg)",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("statusA", {
      cell: (info) => info.getValue(),
      header: "Status",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    ...(userData?.role?.includes("GINNER")
      ? [
          columnHelper.accessor("actions", {
            cell: ({ row }) => (
              <Link
                color={"#3958DC"}
                fontWeight="800"
                onClick={() =>
                  navigate(`./details/${encodeURIComponent(row.original.id)}`)
                }
              >
                View Details
              </Link>
            ),
            header: "Actions",
          }),
        ]
      : []),
  ];

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value);
    // Store the itemRank info
    addMeta({
      itemRank,
    });
    // Return if the item should be filtered in/out
    return itemRank.passed;
  };

  const table = useReactTable({
    data: updatedData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // getFacetedRowModel: getFacetedRowModel(),
    // getFacetedUniqueValues: getFacetedUniqueValues(),
    // getFacetedMinMaxValues: getFacetedMinMaxValues(),
    // Disable "Creating Table Instance" message
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    updateIncomingData();
  }, [appliedList]);

  return (
    <Box>
      <>
        <Box marginTop={"3rem"}>
          <Flex mb="1rem" flexDirection={"row"}>
            <Text fontWeight="800" color={"#141D43"}>
              {" "}
              Procurements{" "}
            </Text>
            <Text fontWeight={"bold"}></Text>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <Box>
              <InputGroup>
                <InputLeftElement
                  height={"100%"}
                  pointerEvents="none"
                  children={<SearchIcon color="#B8DEF8" />}
                />
                <Input
                  type="text"
                  color={"#464C64"}
                  bg={"#fff"}
                  w={"500px"}
                  h={"48px"}
                  value={globalFilter ?? ""}
                  placeholder="Search All Results"
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  fontSize={"14px"}
                  borderRadius={"10px"}
                  _placeholder={{
                    opacity: 1,
                    color: "#464C64",
                    fontSize: "14px",
                  }}
                />
              </InputGroup>
            </Box>
            <Box>
              <Box display={"flex"} justifyContent="space-between">
                <Box>
                  <Box
                    display={"flex"}
                    alignItems="center"
                    marginBottom={"10px"}
                  >
                    <Box
                      as="span"
                      display="flex"
                      alignItems="center"
                      gap="5px"
                      // marginRight={"5px"}
                    >
                      <Box as="span" width={"30%"}>
                        {" "}
                        Go to page:{" "}
                      </Box>
                      <Input
                        w={"60%"}
                        type="number"
                        color={"#464C64"}
                        bg={"#fff"}
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          table.setPageIndex(page);
                        }}
                      />
                    </Box>
                    <Box>
                      <Select
                        bg="#fff"
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                          table.setPageSize(Number(e.target.value));
                        }}
                      >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                          </option>
                        ))}
                      </Select>
                    </Box>
                    {userData?.role?.includes("GINNER") && (
                      <Box>
                        <Button
                          w="180px"
                          bg="#52BBFF"
                          color={"#fff"}
                          marginLeft={"1rem"}
                          _hover={{ bg: "#52BBFF" }}
                          onClick={() => navigate("./procurement-form")}
                        >
                          Add Procurement
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Flex>
        </Box>

        {updatedData?.length > 0 ? (
          <Box marginTop={"2rem"}>
            <Box
              as="span"
              display="flex"
              alignItems="center"
              marginBottom={"10px"}
              gap="5px"
            >
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </Box>
            <Tabs size="md" variant="enclosed">
              <TableContainer className="overflow-style" overflowX={"scroll"}>
                <Table bg="brand.white" borderRadius="15px">
                  <Thead fontStyle={"normal"}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                          const meta = header.column.columnDef.meta;
                          return (
                            <Th
                              key={header.id}
                              color="brand.blue"
                              textTransform="capitalize"
                              onClick={header.column.getToggleSortingHandler()}
                              isNumeric={meta?.isNumeric}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <Box as="span" pl="1rem">
                                {header.column.getIsSorted() ? (
                                  header.column.getIsSorted() === "desc" ? (
                                    <TriangleDownIcon
                                      aria-label="sorted descending"
                                      color={"#3958DC"}
                                      size="20"
                                    />
                                  ) : (
                                    <TriangleUpIcon
                                      aria-label="sorted ascending"
                                      color={"#3958DC"}
                                      size="20"
                                    />
                                  )
                                ) : null}
                              </Box>
                            </Th>
                          );
                        })}
                      </Tr>
                    ))}
                  </Thead>

                  <Tbody>
                    {isloading ? (
                      <>
                        <Tr>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                          <Td>
                            <Skeleton height="20px" fadeDuration={1} />
                          </Td>
                        </Tr>
                      </>
                    ) : (
                      <>
                        {table.getRowModel()?.rows?.map((row) => (
                          <Tr key={row?.id}>
                            {row.getVisibleCells().map((cell) => {
                              // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                              const meta = cell?.column?.columnDef?.meta;
                              return (
                                <Td
                                  key={cell?.id}
                                  isNumeric={meta?.isNumeric}
                                  fontWeight={500}
                                  fontSize="sm"
                                  style={{
                                    maxWidth: "250px",
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                  }}
                                >
                                  {cell?.renderValue() === "actions"
                                    ? userData?.role?.includes("GINNER") && (
                                        <Link
                                          color={"#3958DC"}
                                          fontWeight="800"
                                          onClick={() =>
                                            navigate(
                                              `./details/${encodeURIComponent(
                                                cell?.row?.original?.id
                                              )}`
                                            )
                                          }
                                        >
                                          View Details
                                        </Link>
                                      )
                                    : flexRender(
                                        cell?.column?.columnDef?.cell,
                                        cell?.getContext()
                                      )}
                                </Td>
                              );
                            })}
                          </Tr>
                        ))}
                      </>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <Box
                display={"flex"}
                justifyContent="center"
                marginTop={"1rem"}
                marginBottom="2rem"
              >
                <Box display={"flex"} gap="2rem">
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <AiOutlineArrowLeft color="#141D43" size={18} />
                  </Button>
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    <AiOutlineArrowRight color="#141D43" size={18} />
                  </Button>
                </Box>
              </Box>
            </Tabs>
          </Box>
        ) : (
          <Box
            display={"flex"}
            alignItems="center"
            justifyContent="center"
            mt="2rem"
            bg="white"
            borderRadius="15px"
          >
            <Text
              fontSize={"25px"}
              fontWeight={"bold"}
              textAlign="center"
              p="2rem"
            >
              There are no procurements yet.
            </Text>
          </Box>
        )}
      </>
    </Box>
  );
}

export default isAllowed(AppliedProcurements);
